import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Session, User } from '@supabase/supabase-js';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

// Durée d'inactivité maximale avant déconnexion (30 minutes)
const MAX_INACTIVITY_TIME = 30 * 60 * 1000; 

// Intervalle de rafraîchissement de session (10 minutes)
const SESSION_REFRESH_INTERVAL = 10 * 60 * 1000;

type AuthContextType = {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isCreator: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  signup: (email: string, password: string, name: string) => Promise<void>;
  redirectToAuth: (reason: string, returnPath?: string) => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

type AuthProviderProps = {
  children: ReactNode;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [lastActivity, setLastActivity] = useState<number>(Date.now());
  const navigate = useNavigate();

  // Mettre à jour l'horodatage de dernière activité
  const updateLastActivity = () => {
    setLastActivity(Date.now());
  };

  // Rediriger vers la page d'authentification avec un chemin de retour
  const redirectToAuth = (reason: string, returnPath?: string) => {
    const currentPath = returnPath || window.location.pathname + window.location.search;
    const encodedReturnPath = encodeURIComponent(currentPath);
    
    // Stocker la raison de la redirection pour l'afficher sur la page d'authentification
    sessionStorage.setItem('auth_reason', reason);
    
    // Naviguer vers la page de connexion avec le chemin de retour
    navigate(`/auth/login?returnTo=${encodedReturnPath}`);
  };

  // Vérifier le rôle de l'utilisateur de manière sécurisée
  const checkUserRole = async (userId: string): Promise<boolean> => {
    try {
      console.log("Checking user role for ID:", userId);
      
      // Utiliser la fonction RPC sécurisée pour vérifier le rôle
      const { data: isUserAdmin, error } = await supabase.rpc('is_admin_user');
      
      if (error) {
        console.error("Error checking admin status:", error);
        return false;
      }
      
      console.log("Admin status check result:", isUserAdmin);
      return !!isUserAdmin;
    } catch (error) {
      console.error("Unexpected error checking user role:", error);
      return false;
    }
  };

  // Écouter les événements d'activité de l'utilisateur
  useEffect(() => {
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'];
    
    const handleUserActivity = () => {
      updateLastActivity();
    };

    events.forEach(event => {
      window.addEventListener(event, handleUserActivity);
    });

    // Vérification périodique d'inactivité
    const inactivityCheck = setInterval(() => {
      if (session && Date.now() - lastActivity > MAX_INACTIVITY_TIME) {
        console.log("User inactive for too long, logging out...");
        logout();
        toast.info("Vous avez été déconnecté en raison d'inactivité");
      }
    }, 60000); // Vérifier chaque minute

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });
      clearInterval(inactivityCheck);
    };
  }, [session, lastActivity]);

  // Configuration principale de l'authentification
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, currentSession) => {
        console.log("AuthContext - Auth state changed:", { event: _event, session: currentSession });
        
        setSession(currentSession);
        setUser(currentSession?.user || null);
        
        if (currentSession?.user) {
          setTimeout(async () => {
            const isUserAdmin = await checkUserRole(currentSession.user.id);
            setIsAdmin(isUserAdmin);
          }, 0);
        } else {
          setIsAdmin(false);
        }
        
        setIsLoading(false);
        updateLastActivity();

        // Notifier les autres sous-domaines des changements d'auth
        if (typeof window !== 'undefined') {
          // Utiliser setTimeout pour éviter les blocages
          setTimeout(() => {
            window.postMessage({
              type: 'AUTH_STATE_CHANGE',
              payload: { session: currentSession, event: _event }
            }, window.location.origin);
          }, 0);
        }
      }
    );

    // Écouteur pour les messages cross-domain
    const handleMessage = (event: MessageEvent) => {
      // Vérifier l'origine pour la sécurité (domaines recut.art seulement)
      if (!event.origin.includes('recut.art')) return;

      if (event.data.type === 'AUTH_STATE_CHANGE') {
        const { session: newSession, event: authEvent } = event.data.payload;
        
        // Éviter les boucles infinies
        if (authEvent === 'SIGNED_OUT' && !session) return;
        if (authEvent === 'SIGNED_IN' && session?.access_token === newSession?.access_token) return;
        
        // Synchroniser la session
        if (newSession && !session) {
          supabase.auth.setSession(newSession);
        } else if (!newSession && session) {
          supabase.auth.signOut();
        }
      }
    };

    window.addEventListener('message', handleMessage);

    const initializeAuth = async () => {
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        
        setSession(currentSession);
        setUser(currentSession?.user || null);
        
        if (currentSession?.user) {
          const isUserAdmin = await checkUserRole(currentSession.user.id);
          setIsAdmin(isUserAdmin);
        }
      } catch (error) {
        console.error('Error getting auth session:', error);
      } finally {
        setIsLoading(false);
      }
    };

    // Rafraîchir périodiquement le token pour maintenir la session active
    const sessionRefreshInterval = setInterval(async () => {
      if (session) {
        try {
          const { error } = await supabase.auth.refreshSession();
          if (error) {
            console.error("Error refreshing auth session:", error);
          } else {
            console.log("Auth session refreshed successfully");
          }
        } catch (error) {
          console.error("Unexpected error refreshing session:", error);
        }
      }
    }, SESSION_REFRESH_INTERVAL);

    initializeAuth();

    return () => {
      subscription.unsubscribe();
      clearInterval(sessionRefreshInterval);
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Méthodes d'authentification sécurisées
  const login = async (email: string, password: string) => {
    setIsLoading(true);
    
    try {
      console.log("AuthContext - Login attempt with:", email);
      
      // Sanitize inputs
      const sanitizedEmail = email.trim().toLowerCase();
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email: sanitizedEmail,
        password,
      });
      
      if (error) throw error;
      
      if (data.user) {
        const isUserAdmin = await checkUserRole(data.user.id);
        setIsAdmin(isUserAdmin);
      }
      
      toast.success(`Connexion réussie. Bienvenue !`);
      updateLastActivity();
      
    } catch (error: any) {
      console.error("AuthContext - Login error:", error);
      toast.error(error.message || 'Une erreur est survenue lors de la connexion');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      console.log("AuthContext - Attempting to sign out");
      setIsLoading(true);
      
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error("AuthContext - Sign out error:", error);
        toast.error(error.message || 'Une erreur est survenue lors de la déconnexion');
      } else {
        setUser(null);
        setSession(null);
        setIsAdmin(false);
        toast.success('Déconnexion réussie. Vous avez été déconnecté avec succès.');
      }
    } catch (error: any) {
      console.error("AuthContext - Unexpected error during logout:", error);
      toast.error(error.message || 'Une erreur est survenue lors de la déconnexion');
    } finally {
      setIsLoading(false);
    }
  };

  const signup = async (email: string, password: string, name: string) => {
    setIsLoading(true);
    
    try {
      // Validation côté client
      if (password.length < 8) {
        throw new Error("Le mot de passe doit comporter au moins 8 caractères");
      }
      
      if (!/[A-Z]/.test(password) || !/[0-9]/.test(password)) {
        throw new Error("Le mot de passe doit contenir au moins une majuscule et un chiffre");
      }
      
      // Sanitize inputs
      const sanitizedEmail = email.trim().toLowerCase();
      const sanitizedName = name.trim();
      
      const { data, error } = await supabase.auth.signUp({
        email: sanitizedEmail,
        password,
        options: {
          data: {
            full_name: sanitizedName,
            role: 'creator',
          },
        },
      });
      
      if (error) throw error;
      
      toast.success('Compte créé avec succès. Veuillez vérifier votre e-mail pour confirmer votre compte.');
      
    } catch (error: any) {
      toast.error(error.message || 'Une erreur est survenue lors de l\'inscription');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Fonction sécurisée de vérification des comptes administrateurs
  useEffect(() => {
    const checkAdminAccounts = async () => {
      try {
        if (!user) return;
        
        const { data: isUserAdmin, error } = await supabase.rpc('is_admin_user');
        
        if (error) {
          console.error("Error checking admin status:", error);
          return;
        }
        
        if (isUserAdmin && !isAdmin) {
          console.log("Admin status corrected based on secure RPC check");
          setIsAdmin(true);
        }
      } catch (error) {
        console.error("Unexpected error checking admin accounts:", error);
      }
    };
    
    if (user) {
      checkAdminAccounts();
    }
  }, [user, isAdmin]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      session,
      isLoading, 
      isAuthenticated: !!user,
      isAdmin,
      isCreator: !!user,
      login, 
      logout, 
      signup,
      redirectToAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};
